/**
 * Reintento de descarga por serie (Explorer): cache IDB, cornerstone, skeleton en store.
 */
import cornerstone from 'cornerstone-core'
import { deleteCachedSops } from '../db/dicomCache'
import { loadInstanceForMode } from './loadInstanceForMode'
import { loadDicomInstanceDirectUrl } from './loadDicomInstanceWithCache'
import { RENDER_MODE } from './renderMode'

/**
 * @param {object[]} files
 * @returns {string[]}
 */
export function collectSopsFromSeriesFiles(files) {
  if (!Array.isArray(files)) return []
  const out = []
  const seen = new Set()
  for (const f of files) {
    if (!f) continue
    const sop =
      f.sopInstanceUID ||
      (f.inst && f.inst.sopInstanceUID) ||
      null
    if (sop && !seen.has(sop)) {
      seen.add(sop)
      out.push(String(sop))
    }
  }
  return out
}

/**
 * Purga imageCache cornerstone por imageId / previewUrl de la serie.
 * @param {object[]} files
 */
export function purgeCornerstoneCacheForSeriesFiles(files) {
  if (!Array.isArray(files) || !cornerstone.imageCache) return
  for (const f of files) {
    if (!f) continue
    const ids = []
    if (f.imageId) ids.push(String(f.imageId))
    if (f.previewUrl) ids.push(String(f.previewUrl))
    for (const id of ids) {
      try {
        if (typeof cornerstone.imageCache.removeImageLoadObject === 'function') {
          cornerstone.imageCache.removeImageLoadObject(id)
        }
      } catch (e) {
        /* ignore */
      }
    }
  }
}

/**
 * Resetea items para re-descarga (sin cancelar estudio global).
 * @param {object[]} files
 * @returns {object[]}
 */
export function resetSeriesFilesForRetry(files) {
  if (!Array.isArray(files)) return []
  return files.map((f) => {
    if (!f || typeof f !== 'object') return f
    const next = { ...f }
    delete next.image
    next.skeleton = true
    next._retryGen = (Number(next._retryGen) || 0) + 1
    return next
  })
}

/**
 * @param {object[]} files
 * @returns {Promise<object[]>}
 */
export async function prepareSeriesRetry(files) {
  const sops = collectSopsFromSeriesFiles(files)
  await deleteCachedSops(sops)
  purgeCornerstoneCacheForSeriesFiles(files)
  return resetSeriesFilesForRetry(files)
}

/**
 * Aplica retry en el array global de files del store (por seriesInstanceUID o seriesNumber).
 * @param {object[]} allFiles
 * @param {object[]} seriesFiles
 * @returns {object[]}
 */
export function mergeRetriedSeriesIntoStore(allFiles, seriesFiles) {
  if (!Array.isArray(allFiles) || !Array.isArray(seriesFiles) || !seriesFiles.length) {
    return allFiles
  }
  const sample = seriesFiles[0]
  const seriesUid =
    (sample.series && sample.series.seriesInstanceUID) ||
    sample.seriesInstanceUID ||
    null
  const sn =
    sample.series && sample.series.seriesNumber != null
      ? String(sample.series.seriesNumber)
      : null

  const retried = resetSeriesFilesForRetry(seriesFiles)
  const retriedBySop = new Map()
  retried.forEach((f) => {
    if (f && f.sopInstanceUID) retriedBySop.set(String(f.sopInstanceUID), f)
  })

  return allFiles.map((f) => {
    if (!f) return f
    const matchUid =
      seriesUid &&
      ((f.series && f.series.seriesInstanceUID === seriesUid) ||
        f.seriesInstanceUID === seriesUid)
    const matchSn =
      sn &&
      f.series &&
      f.series.seriesNumber != null &&
      String(f.series.seriesNumber) === sn
    if (!matchUid && !matchSn) return f
    const sop = f.sopInstanceUID ? String(f.sopInstanceUID) : null
    if (sop && retriedBySop.has(sop)) return { ...retriedBySop.get(sop) }
    return f
  })
}

/**
 * @param {object|null} manifest
 * @param {string} sopUid
 * @returns {{ series: object, inst: object }|null}
 */
export function findManifestInstContext(manifest, sopUid) {
  if (!manifest || !Array.isArray(manifest.series) || !sopUid) return null
  const want = String(sopUid).trim()
  for (let si = 0; si < manifest.series.length; si++) {
    const series = manifest.series[si]
    const instances = series.instances || []
    for (let ii = 0; ii < instances.length; ii++) {
      const inst = instances[ii]
      if (!inst) continue
      const sop =
        inst.sopInstanceUID ||
        inst.SOPInstanceUID ||
        inst.sop ||
        null
      if (sop && String(sop).trim() === want) {
        return { series, inst }
      }
    }
  }
  return null
}

/**
 * Re-fetch de un frame tras purge (manifest o URL directa).
 * @param {object} file
 * @param {object} [opts]
 * @returns {Promise<object>}
 */
export async function refetchSeriesFile(file, opts = {}) {
  const manifest = opts.manifest || null
  const assetBaseUrl = opts.assetBaseUrl || null
  const renderMode = opts.renderMode || RENDER_MODE.DIAGNOSTIC
  const sop = file && file.sopInstanceUID ? String(file.sopInstanceUID) : null
  const ctx = sop && manifest ? findManifestInstContext(manifest, sop) : null

  if (ctx) {
    const loaded = await loadInstanceForMode({
      inst: ctx.inst,
      assetBaseUrl,
      studyInstanceUID: manifest.studyInstanceUID || null,
      renderMode,
      absoluteUrl: file.dicomUrl || null,
      skipIdbRead: true,
    })
    return {
      image: loaded.image,
      imageId: loaded.imageId,
      skeleton: false,
      error: null,
      fromCache: !!loaded.fromCache,
      renderKind: loaded.renderKind || loaded.loadKind || 'dicom',
    }
  }

  const url =
    file.dicomUrl ||
    (file.imageId && String(file.imageId).replace(/^wadouri:/i, '')) ||
    file.previewUrl ||
    null
  if (!url) {
    throw new Error('refetchSeriesFile: sin URL')
  }
  const direct = await loadDicomInstanceDirectUrl(url)
  return {
    image: direct.image,
    imageId: direct.imageId,
    skeleton: false,
    error: null,
    renderKind: 'dicom',
  }
}

/**
 * @param {object[]} seriesFiles
 * @param {object} [opts]
 * @returns {Promise<object[]>}
 */
export async function refetchExplorerSeriesFiles(seriesFiles, opts = {}) {
  if (!Array.isArray(seriesFiles) || !seriesFiles.length) return []
  const parallel = Math.max(1, Math.min(6, opts.parallel || 3))
  const queue = seriesFiles.slice()
  const patches = []

  async function worker() {
    while (queue.length) {
      const file = queue.shift()
      if (!file) continue
      try {
        const patch = await refetchSeriesFile(file, opts)
        patches.push({ file, patch })
        if (typeof opts.onFileLoaded === 'function') {
          opts.onFileLoaded(file, patch)
        }
      } catch (e) {
        patches.push({ file, error: e })
      }
    }
  }

  const workers = []
  for (let i = 0; i < parallel; i++) workers.push(worker())
  await Promise.all(workers)
  return patches
}
